import {
  Goal,
  ID,
  LifeArea,
  lifeAreaOtherID,
  newTaskSuffix,
  rootId,
  SortingMode,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getSortedTasks } from '../get-sorted-tasks';
import { filterEmptyChildrenCascading } from './filter-empty-children-cascading';

const getGoalLifeArea = (goal: Goal, goals: Goal[]): ID | undefined => {
  if (goal.lifeAreaId || !goal.parentIds?.length) {
    return goal.lifeAreaId ?? undefined;
  }

  const parentGoal = goals.find((g) => g.id === goal.parentIds?.[0]);
  return parentGoal ? getGoalLifeArea(parentGoal, goals) : undefined;
};

export type BuildLifeAreaTreeOptions = {
  lifeAreas: LifeArea[];
  goals: Goal[];
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
  otherLifeAreaLabel: string;
  rootLabel: string;
  newTaskLabel: string;
};

export const buildLifeAreaTree = ({
  lifeAreas,
  goals,
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
  otherLifeAreaLabel,
  rootLabel,
  newTaskLabel,
}: BuildLifeAreaTreeOptions): Record<ID, TaskListTreeItem> => {
  const lifeAreaTreeItems = lifeAreas.reduce<Record<ID, TaskListTreeItem>>(
    (acc, lifeArea) => {
      acc[lifeArea.id] = {
        id: lifeArea.id,
        type: TaskListItemTypes.LifeArea,
        name: lifeArea.name,
        children: [],
        isFolder: true,
      };
      return acc;
    },
    {},
  );
  lifeAreaTreeItems[lifeAreaOtherID] = {
    id: lifeAreaOtherID,
    type: TaskListItemTypes.LifeArea,
    name: otherLifeAreaLabel,
    children: [],
    isFolder: true,
  };

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? Object.keys(lifeAreaTreeItems).reduce<ID[]>((acc, lifeAreaId) => {
          const lifeAreaOrder = customOrder?.[lifeAreaId] ?? [];
          acc.push(...lifeAreaOrder);
          return acc;
        }, [])
      : [];

  const allTreeItems = getSortedTasks(tasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
  }).reduce<Record<ID, TaskListTreeItem>>((acc, task) => {
    const parentGoal = task.goalId
      ? goals.find((g) => g.id === task.goalId)
      : undefined;
    const parentLifeAreaId =
      (parentGoal ? getGoalLifeArea(parentGoal, goals) : task.lifeAreaId) ??
      lifeAreaOtherID;
    const parentTreeItem = acc[parentLifeAreaId];
    if (parentTreeItem) {
      parentTreeItem.children?.push(task.id);
      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TaskListItemTypes.Task,
      };
    }
    return acc;
  }, lifeAreaTreeItems);

  if (!hideNewTaskRow) {
    Object.keys(lifeAreaTreeItems).forEach((lifeAreaId) => {
      const parentTreeItem = allTreeItems[lifeAreaId];
      if (!parentTreeItem) {
        return;
      }
      if (!parentTreeItem.children?.length && !showEmptyGroups) {
        return;
      }

      const newTaskItemId = `${lifeAreaId}${newTaskSuffix}`;
      parentTreeItem.children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    });
  }

  const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
  const topLevelItemIds = Object.keys(filteredTreeItems).filter(
    (id) => filteredTreeItems[id].type === TaskListItemTypes.LifeArea,
  );

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: topLevelItemIds,
    },
    ...filteredTreeItems,
  };
};
